import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { useEffect, useState, memo } from "react";

// 🟢 Moved AnimatedNumber OUTSIDE TimerSection to prevent re-renders
const AnimatedNumber = memo(({ value }) => {
  return (
    <motion.span
      key={value} // Key ensures re-animation only when value changes
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0, transition: { type: "spring", stiffness: 200, damping: 15 } }}
      exit={{ opacity: 0, y: 10, transition: { duration: 0.2 } }}
      style={{
        display: "inline-block",
        minWidth: 30,
        textAlign: "center",
      }}
    >
      {value}
    </motion.span>
  );
});

const ValueCard = ({ value, label }) => {
  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      boxShadow: 2,
      borderRadius: 2,
      width: "100px",
      py: 1,
      background: "linear-gradient(45deg,rgb(240, 205, 239) 30%,rgb(244, 210, 191) 90%)",
    }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        <AnimatedNumber value={value} />
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {label}
      </Typography>
    </Box>
  );
}

// 🟢 Main Timer Section
const TimerSection = ({ meetingDate }) => {
  const [elapsed, setElapsed] = useState({
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isActivated: false,
  });

  const calculateElapsed = () => {
    const now = dayjs();
    const diffInSeconds = Math.floor(now.diff(meetingDate) / 1000);

    const years = Math.floor(diffInSeconds / (3600 * 24 * 365));
    const months = Math.floor((diffInSeconds % (3600 * 24 * 365)) / (3600 * 24 * 30) % 12);
    const days = Math.floor(diffInSeconds / (3600 * 24) / 365);
    const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    setElapsed((prev) => ({
      years: prev.years !== years ? years : prev.years,
      months: prev.months !== months ? months : prev.months,
      days: prev.days !== days ? days : prev.days,
      hours: prev.hours !== hours ? hours : prev.hours,
      minutes: prev.minutes !== minutes ? minutes : prev.minutes,
      seconds: prev.seconds !== seconds ? seconds : prev.seconds,
      isActivated: true,
    }));
  }

  useEffect(() => {
    // 🟢 Initial calculation
    calculateElapsed();

    // 🟢 Calculate elapsed time every second
    const interval = setInterval(() => {
      calculateElapsed();
    }, 1000);

    // 🟢 Clear interval when component unmounts
    return () => clearInterval(interval);
  }, [meetingDate]);

  if (!elapsed.isActivated) {
    return null;
  }

  return (
    <Box sx={{
      zIndex: 1,
      position: "relative",
      p: 4,
      mt: 4,
      textAlign: "center",
      minHeight: "100dvh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      scrollSnapAlign: 'start',
    }}>
      <Typography
        align="center"
        variant="h4"
        color="secondary"
        sx={{ fontWeight: 'bold', mb: 6 }}
      >
        เราคบกันมานานแค่ไหนแล้ว 🕰️ 🗓️
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <ValueCard value={elapsed.years} label="Years" />
        <ValueCard value={elapsed.months} label="Months" />
        <ValueCard value={elapsed.days} label="Days" />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        <ValueCard value={elapsed.hours} label="Hours" />
        <ValueCard value={elapsed.minutes} label="Minutes" />
        <ValueCard value={elapsed.seconds} label="Seconds" />
      </Box>
    </Box>
  );
};

export default TimerSection;
